import { useEffect, useState, type PropsWithChildren } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Menu } from "lucide-react";
import { NAV_GROUPS, landingHrefOf, modulesInGroup } from "@/modules";
import type { AdminModule } from "@/modules/types";
import { logout } from "@/lib/session";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSession } from "./session-context";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { useCurrentModule } from "./useCurrentModule";

/**
 * One nav, rendered in two places.
 *
 * On desktop it is a fixed rail. On a phone the same tree goes inside a Sheet
 * behind the header's menu button — the old shell had a hard-coded `w-60`
 * aside with no breakpoint at all, so at 390px it took 240px of the viewport
 * and left the content 150px.
 */
export function AppShell({ children }: PropsWithChildren) {
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const current = useCurrentModule();

  // Navigating from inside the drawer must close it, or the destination
  // renders behind an overlay the user then has to dismiss by hand.
  useEffect(() => {
    setDrawerOpen(false);
  }, [loc.pathname]);

  return (
    <div className="flex min-h-screen w-full">
      {/* Desktop rail */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <div className="sticky top-0 flex h-full max-h-screen flex-col">
          <BrandMark />
          <NavTree className="min-h-0 flex-1 overflow-y-auto px-3 pb-4" />
          <SessionFooter />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[86vw] max-w-xs bg-sidebar p-0" hideClose>
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <div className="flex h-full flex-col">
            <BrandMark />
            <NavTree className="min-h-0 flex-1 overflow-y-auto px-3 pb-4" />
            <SessionFooter />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile header. Sticky so the way back to the nav is always one tap
            away, however far down a long table the user has scrolled. */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{current?.label ?? "NuForce Admin"}</div>
          </div>
        </header>

        <main className="min-w-0 flex-1">
          {/* Keyed on pathname so navigating away from a crashed page clears the
              error instead of stranding the boundary on every later route. */}
          <RouteErrorBoundary key={loc.pathname}>{children}</RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex h-14 shrink-0 items-center gap-2 border-b border-sidebar-border px-5">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">NuForce</div>
        <div className="-mt-0.5 text-sm font-semibold text-foreground">Admin</div>
      </div>
    </div>
  );
}

function NavTree({ className }: { className?: string }) {
  const session = useSession();

  return (
    <nav className={cn("space-y-4 pt-3", className)}>
      {NAV_GROUPS.map((group) => {
        const items = modulesInGroup(group.key, session);
        if (items.length === 0) return null;
        // A heading over a lone module of the same name says nothing twice.
        // Revenue is that case today: one module, one group, one word.
        const redundant = items.length === 1 && items[0].label === group.label;
        return (
          <div key={group.key} className="space-y-1">
            {group.label && !redundant && (
              <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                {group.label}
              </div>
            )}
            {items.map((m) => (
              <NavItem key={m.key} module={m} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function NavItem({ module: m }: { module: AdminModule }) {
  const session = useSession();
  const loc = useLocation();
  const Icon = m.icon;
  // NavLink's own matching is per-href; a module is active for any path under
  // its key, including detail routes the sidebar never links to directly.
  const active = loc.pathname === `/${m.key}` || loc.pathname.startsWith(`/${m.key}/`);

  return (
    <NavLink
      to={landingHrefOf(m, session)}
      className={cn(
        // 40px tall: a nav row is the most-tapped target in the app.
        "flex h-10 items-center gap-3 rounded-md px-3 text-sm transition-colors",
        active
          ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
          : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      )}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active && "text-link")} />
      <span className="truncate">{m.label}</span>
    </NavLink>
  );
}

function SessionFooter() {
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function handleLogout() {
    await logout();
    qc.removeQueries({ queryKey: ["admin-session"] });
    navigate("/login", { replace: true });
  }

  return (
    <div className="shrink-0 border-t border-sidebar-border p-3 pb-safe">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-foreground">{session.user.display_name}</div>
          <div className="truncate text-xs text-muted-foreground">{session.user.username}</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          aria-label="Sign out"
          className="text-muted-foreground hover:text-red-400"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
